import type { StudyMetadataResponse } from "../utils/dicomMetadataLoader";
import type { ResolvedPredictionMask } from "./segmentationMask";

export type Point2 = [number, number];

export interface SegmentationPreview {
  id: string;
  segmentIndex: number;
  sourceSegmentIndex: number;
  condition: string;
  seriesInstanceUid: string;
  seriesNumber: number;
  sliceIndex: number;
  sliceNumber: number;
  frameOfReferenceUid: string | null;
  worldCentroid?: [number, number, number];
  pixelCentroid?: [number, number];
  caseProbability: number;
  voxelCount: number;
  maskRows: number;
  maskColumns: number;
  maskRuns: [number, number][];
  measurements: {
    longAxisMm: number | null;
    shortAxisMm: number | null;
    volumeMm3: number | null;
  };
  cropBoundsPx: { x: number; y: number; w: number; h: number };
  longAxisPx: [Point2, Point2];
  shortAxisPx: [Point2, Point2];
}

function parsePixelSpacing(value: string | null | undefined): [number, number] | null {
  if (!value) return null;
  const values = value.split("\\").map(Number);
  if (values.length !== 2 || values.some((item) => !Number.isFinite(item) || item <= 0)) {
    return null;
  }
  return [values[0], values[1]];
}

function runBounds(
  runs: readonly [number, number][],
  columns: number,
): { minRow: number; maxRow: number; minColumn: number; maxColumn: number } | null {
  let minRow = Infinity;
  let maxRow = -Infinity;
  let minColumn = Infinity;
  let maxColumn = -Infinity;
  for (const [start, length] of runs) {
    let cursor = start;
    const end = start + length;
    while (cursor < end) {
      const row = Math.floor(cursor / columns);
      const column = cursor % columns;
      const rowEnd = Math.min(end, (row + 1) * columns);
      minRow = Math.min(minRow, row);
      maxRow = Math.max(maxRow, row);
      minColumn = Math.min(minColumn, column);
      maxColumn = Math.max(maxColumn, column + (rowEnd - cursor) - 1);
      cursor = rowEnd;
    }
  }
  return Number.isFinite(minRow) ? { minRow, maxRow, minColumn, maxColumn } : null;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Build truthful, case-linked card data from the fields emitted by a2z-rle-v1. */
export function buildSegmentationPreviews(
  mask: ResolvedPredictionMask | null,
  metadata: StudyMetadataResponse | null | undefined,
  probabilitiesByCondition: Readonly<Record<string, number>>,
  idScope?: string,
): SegmentationPreview[] {
  if (!mask || !metadata) return [];

  return mask.segments.flatMap((segment) => {
    const representative = [...segment.slices].sort(
      (a, b) =>
        b.runs.reduce((sum, [, length]) => sum + length, 0) -
        a.runs.reduce((sum, [, length]) => sum + length, 0),
    )[0];
    if (!representative) return [];

    const bounds = runBounds(representative.runs, mask.columns);
    const seriesMetadata = metadata.series_metadata[representative.seriesId];
    const instance = metadata.series[representative.seriesId]?.[representative.sliceIndex];
    if (!bounds || !seriesMetadata || !instance) return [];

    const nativeRows = instance.rows ?? mask.rows;
    const nativeColumns = instance.columns ?? mask.columns;
    const xScale = nativeColumns / mask.columns;
    const yScale = nativeRows / mask.rows;
    const minX = bounds.minColumn * xScale;
    const maxX = (bounds.maxColumn + 1) * xScale;
    const minY = bounds.minRow * yScale;
    const maxY = (bounds.maxRow + 1) * yScale;
    const width = Math.max(1, maxX - minX);
    const height = Math.max(1, maxY - minY);
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    // The original preview framing used a 48px minimum / 4x segment margin.
    // Halving both produces a 2x (100% additional) thumbnail zoom while the
    // rendered card remains 132px square.
    const cropSize = Math.min(
      nativeRows,
      nativeColumns,
      Math.max(24, Math.ceil(Math.max(width, height) * 2)),
    );
    const cropX = clamp(Math.round(centerX - cropSize / 2), 0, nativeColumns - cropSize);
    const cropY = clamp(Math.round(centerY - cropSize / 2), 0, nativeRows - cropSize);

    const horizontal: [Point2, Point2] = [
      [minX, centerY],
      [maxX, centerY],
    ];
    const vertical: [Point2, Point2] = [
      [centerX, minY],
      [centerX, maxY],
    ];
    const spacing = parsePixelSpacing(seriesMetadata.pixel_spacing ?? instance.pixel_spacing);
    const orientation =
      seriesMetadata.image_orientation_patient ?? instance.image_orientation_patient;
    const ipp = instance.image_position_patient;
    const worldCentroid =
      spacing &&
      ipp?.length === 3 &&
      orientation?.length === 6 &&
      orientation.every(Number.isFinite)
        ? ([
            ipp[0] + centerX * spacing[1] * orientation[0] + centerY * spacing[0] * orientation[3],
            ipp[1] + centerX * spacing[1] * orientation[1] + centerY * spacing[0] * orientation[4],
            ipp[2] + centerX * spacing[1] * orientation[2] + centerY * spacing[0] * orientation[5],
          ] as [number, number, number])
        : undefined;
    const widthMm = spacing ? width * spacing[1] : null;
    const heightMm = spacing ? height * spacing[0] : null;
    const longAxisMm = widthMm == null || heightMm == null ? null : Math.max(widthMm, heightMm);
    const shortAxisMm = widthMm == null || heightMm == null ? null : Math.min(widthMm, heightMm);
    const voxelVolume = spacing
      ? spacing[0] * spacing[1] * (seriesMetadata.slice_thickness ?? 1)
      : null;

    return [
      {
        // Keep existing renderer IDs for non-factory persisted UI state.
        // Candidate API identity uses condition + sourceSegmentIndex below.
        id: `${idScope ? `${idScope}::` : ""}prediction-segment-${segment.segmentIndex}`,
        segmentIndex: segment.segmentIndex,
        sourceSegmentIndex: segment.sourceSegmentIndex,
        seriesInstanceUid: representative.seriesId,
        seriesNumber: seriesMetadata.series_number,
        sliceIndex: representative.sliceIndex,
        sliceNumber: representative.sliceIndex + 1,
        frameOfReferenceUid: seriesMetadata.frame_of_reference_uid,
        worldCentroid,
        pixelCentroid: [centerX, centerY],
        condition: segment.condition,
        caseProbability: probabilitiesByCondition[segment.condition] ?? 0,
        voxelCount: segment.voxelCount,
        maskRows: mask.rows,
        maskColumns: mask.columns,
        maskRuns: representative.runs,
        measurements: {
          longAxisMm,
          shortAxisMm,
          volumeMm3: voxelVolume == null ? null : segment.voxelCount * voxelVolume,
        },
        cropBoundsPx: { x: cropX, y: cropY, w: cropSize, h: cropSize },
        longAxisPx: width >= height ? horizontal : vertical,
        shortAxisPx: width >= height ? vertical : horizontal,
      },
    ];
  });
}
